import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Globe,
  FileText,
  // Eye,
  Upload,
  // CheckCircle,
  // XCircle,
  AlertCircle,
  Users,
  FileUser,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { InputFieldset } from "../../../ui/inputFieldset";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import { Switch } from "../../../ui/switch";
import { toast } from "sonner";
import { FullScreenLoading } from "../../../components/ui/loader";
import { getVisaApplicationById } from "../../../services/visaService";
import { MessageSquare, Clock } from "lucide-react";
import {
  getVisaReviewConversation,
  sendVisaReviewMessage,
} from "../../../services/visaService";
import { usePageHeader } from "contexts/PageHeaderContext";

interface VisaApplicantMoreInfoViewPageProps {
  onNavigate?: (page: string) => void;
}

interface MoreInfoDocument {
  documentType: string;
  fileName?: string;
  status: "approved" | "rejected" | "request-more-info" | "pending";
  uploadedDate?: string;
  rejectionRemark?: string;
}

interface MoreInfoApplicant {
  applicationNumber: string;
  itsNo: string;
  loginId?: string;
  applicantType?: string;
  category?: string;
  guardianITS?: string;
  hofITS?: string;
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  age?: number;
  gender?: string;
  birthPlace?: string;
  nationality?: string;
  profession?: string;

  passportNo?: string;
  passportType?: string;
  passportIssuePlace?: string;
  passportIssueDate?: string;
  passportExpiryDate?: string;
  passportFirstName?: string;
  passportMiddleName?: string;
  passportLastName?: string;
  passportArabicName?: string;
  passportDOB?: string;
  passportGender?: string;
  passportNationality?: string;

  isFamilyOpen: boolean;
  fatherName?: string;
  fatherNameArabic?: string;
  grandfatherName?: string;
  grandfatherNameArabic?: string;
  motherName?: string;
  motherNameArabic?: string;
  mothersFatherName?: string;
  mothersFatherNameArabic?: string;

  visaRequired?: boolean;
  isMiqaatApplicant?: boolean; 
  lastStatusMessage?: string;
  appliedDate?: string;
  visaFee?: number;
  currentStatusCode?: string;
  createdOn?: string;

  documents?: MoreInfoDocument[];
}

interface ReviewMessage {
  id: string;
  sender: "backoffice" | "tour_operator";
  senderName: string;
  message: string;
  timestamp: string;
  dateTime: string;
}

export default function VisaApplicantMoreInfoViewPage({
  onNavigate,
}: VisaApplicantMoreInfoViewPageProps) {
  // const [documentUploads, setDocumentUploads] = useState<Record<string, File>>(
  //   {},
  // );

  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const { id } = useParams<{ id: string }>();
  const [applicantData, setApplicantData] = useState<MoreInfoApplicant | null>(
    null,
  );

  const [loading, setLoading] = useState(true);

  const [reviewConversation, setReviewConversation] = useState<ReviewMessage[]>(
    [],
  );
  const [responseToAdmin, setResponseToAdmin] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [conversationLoading, setConversationLoading] = useState(false);

  const [isFamilyOpen, setIsFamilyOpen] = useState(false);

  const toggleFamilySection = () => {
    setIsFamilyOpen(prev => !prev);
  };

  useEffect(() => {
    setPageHeader({
      title: "View Applicant More Info Details",
      icon: <FileUser className="h-4 w-4" />,
      onBack: () => navigate("/visa-applicant-management"),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader]);

  useEffect(() => {
    if (!id) return;

    const fetchDetails = async () => {
      try {
        setLoading(true);

        const response = await getVisaApplicationById(Number(id));
        if (!response) {
          toast.error("Applicant not found");
          setApplicantData(null);
          return;
        }

        setApplicantData(response);
      } catch (error) {
        toast.error("Failed to load applicant details");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const fetchConversation = async () => {
      try {
        setConversationLoading(false);

        const data = await getVisaReviewConversation(Number(id));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mappedMessages: ReviewMessage[] = data.map((item: any) => {
          const date = new Date(item.createdOn);

          return {
            id: item.reviewConversationId.toString(),
            sender: item.isAdmin ? "backoffice" : "tour_operator",
            senderName: item.senderName,
            message: item.message,
            timestamp: date.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            dateTime: date.toLocaleDateString(),
          };
        });

        setReviewConversation(mappedMessages);
      } catch (error) {
        toast.error("Failed to load review conversation");
      } finally {
        setConversationLoading(false);
      }
    };

    fetchConversation();
  }, [id]);

  const handleSendResponse = async () => {
    if (!responseToAdmin.trim()) {
      toast.error("Please add a response before sending");
      return;
    }

    try {
      const payload = {
        visaApplicationId: Number(id),
        message: responseToAdmin,
        isInternal: false, 
      };

      await sendVisaReviewMessage(payload);

      toast.success("Response sent successfully!");

      setResponseToAdmin("");

      const updatedConversation = await getVisaReviewConversation(Number(id));

      const mappedMessages: ReviewMessage[] = updatedConversation.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => {
          const date = new Date(item.createdOn);

          return {
            id: item.reviewConversationId.toString(),
            sender: item.isAdmin ? "backoffice" : "tour_operator",
            senderName: item.senderName,
            message: item.message,
            timestamp: date.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            dateTime: date.toLocaleDateString(),
          };
        }, 
      );

      setReviewConversation(mappedMessages);
    } catch (error) {
      toast.error("Failed to send response");
    }
  };

  const formatStatus = (status?: string) => {
    if (!status) return "-";

    return status
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };



  // const handleDocumentUpload = (
  //   documentType: string,
  //   e: React.ChangeEvent<HTMLInputElement>,
  // ) => {
  //   const files = e.target.files;
  //   if (!files || files.length === 0) return;

  //   const file = files[0];

  //   if (!file.type.includes("jpeg") && !file.type.includes("jpg")) {
  //     toast.error("Only JPEG files are allowed");
  //     return;
  //   }

  //   if (file.size > 5 * 1024 * 1024) {
  //     toast.error("File size should not exceed 5MB");
  //     return;
  //   }

  //   setDocumentUploads((prev) => ({
  //     ...prev,
  //     [documentType]: file,
  //   }));

  //   toast.success(`${documentType} uploaded successfully`);
  // };

  // const handleViewDocument = (file?: string) => {
  //   if (!file) {
  //     toast.error("No document available");
  //     return;
  //   }
  //   toast.info(`Viewing ${file}`);
  // };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleResubmit = () => {
    if (!applicantData || !applicantData.documents) {
      toast.error("Applicant data not loaded");
      return;
    }

    // const documentsNeedingAttention = applicantData.documents.filter(
    //   (doc) => doc.status === "request-more-info" || doc.status === "rejected",
    // );

    // const hasUploadedAll = documentsNeedingAttention.every(
    //   (doc) => documentUploads[doc.documentType],
    // );

    // if (!hasUploadedAll) {
    //   toast.error(
    //     "Please re-upload all documents that require attention before resubmitting.",
    //   );
    //   return;
    // }

    toast.success("Applicant resubmitted successfully for processing!");
    setTimeout(() => {
      onNavigate?.("visa-applicant-management");
    }, 1500);
  };

  if (loading) {
    return (
      <div>
        <FullScreenLoading />
      </div>
    );
  }

  if (!applicantData) {
    return (
      <div className="p-6 text-sm text-red-600">Applicant data not found</div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-3 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4 md:space-y-5">

        {/* Top Info Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 md:p-4 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div>
                <p className="text-xs text-gray-500">Application No.</p>
                <p
                  className="text-sm font-semibold"
                  style={{ color: "#5B9BD5" }}
                >
                  {applicantData.applicationNumber}
                </p>
              </div>
              <div className="hidden sm:block h-8 w-px bg-gray-200" />
              <div>
                <p className="text-xs text-gray-500">ITS Number</p>
                <p className="text-sm font-semibold text-gray-900">
                  {applicantData.itsNo}
                </p>
              </div>
              <div className="hidden sm:block h-8 w-px bg-gray-200" />
              <div>
                <p className="text-xs text-gray-500">Applied Date</p>
                <p className="text-sm font-medium text-gray-900">
                  {applicantData.createdOn?.split("T")[0]}
                </p>
              </div>
              <div className="hidden sm:block h-8 w-px bg-gray-200" />
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <Badge className="px-2 py-0.5 text-xs mt-1 bg-red-100 text-red-700 border-red-200">
                  {formatStatus(applicantData.currentStatusCode)}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs text-gray-500 md:text-right">Visa Cost</p>
                <p className="text-sm font-semibold text-gray-900">
                  ${applicantData.visaFee}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Remarks Section */}
        {applicantData.lastStatusMessage && (
          <Card className="bg-red-50 shadow-sm border-2 border-red-300 rounded-xl">
            <div className="bg-red-100 border-b border-red-200 px-4 py-3 flex items-center gap-0">
              <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-700" />
              <h3 className="text-xs md:text-sm font-semibold text-red-900">
                Admin Remarks - Action Required
              </h3>
            </div>
            <CardContent className="p-4">
              <p className="text-xs md:text-sm text-red-900 leading-relaxed">
                {applicantData.lastStatusMessage}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Review Conversation Log */}
        <Card className="border-2 border-sky-200 shadow-md">
          <CardContent className="p-4 md:p-6">
            <div
              className="flex items-center gap-2 mb-4 pb-3 border-b-2"
              style={{ borderColor: "#5B9BD5" }}
            >
              <MessageSquare
                className="w-4 h-4 md:w-5 md:h-5"
                style={{ color: "#5B9BD5" }}
              />
              <h3 className="text-sm md:text-base font-semibold text-gray-900">
                Review Conversation
              </h3>
            </div>

            {/* Conversation Thread */}
            {reviewConversation && reviewConversation.length > 0 ? (
              <div>
                <div className="bg-gradient-to-br from-blue-50 via-sky-50 to-blue-50 rounded-lg p-4 md:p-5 border-2 border-[#5B9BD5]/30 shadow-sm">
                  <h4 className="text-xs md:text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 pb-2 border-b border-[#5B9BD5]/20">
                    <Clock
                      className="w-3.5 h-3.5 md:w-4 md:h-4"
                      style={{ color: "#5B9BD5" }}
                    />
                    Entire Conversation Log ({reviewConversation.length}{" "}
                    messages)
                  </h4>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                    {reviewConversation.map((msg, index) => {
                      const isLatest = index === reviewConversation.length - 1;

                      return (
                        <div
                          key={msg.id}
                          className={`rounded-xl p-3 md:p-4 shadow-sm border-2 transition-all hover:shadow-md ${
                            msg.sender === "backoffice"
                              ? "bg-gradient-to-br from-blue-50 to-white border-blue-300"
                              : "bg-gradient-to-br from-green-50 to-white border-green-300"
                          } ${
                            isLatest
                              ? "border-2 border-red-500"
                              : ""
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs md:text-sm font-bold text-gray-900">
                                  {msg.senderName}
                                </span>
                                {isLatest && (
                                  <Badge className="bg-red-100 text-red-700 border-red-400 text-[10px] px-2 py-0.5">
                                    Latest
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-[10px] md:text-xs font-semibold text-gray-600">
                                {msg.timestamp}
                              </p>
                              <p className="text-[10px] md:text-xs text-gray-500">
                                {msg.dateTime}
                              </p>
                            </div>
                          </div>
                          <div
                            className={`pl-3 border-l-4 ${
                              msg.sender === "backoffice"
                                ? "border-[#5B9BD5]"
                                : "border-green-500"
                            }`}
                          >
                            <p className="text-xs md:text-sm text-gray-800 leading-relaxed whitespace-pre-line">
                              {msg.message}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-5 md:p-6 border-2 border-dashed border-gray-300">
                <p className="text-xs md:text-sm text-gray-600 text-center">
                  No conversation messages yet. The conversation will appear
                  here once the review process begins.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-blue-50 shadow-sm border border-blue-200 rounded-lg">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5" style={{ color: "#5B9BD5" }} />
              <h3
                className="text-sm font-semibold"
                style={{ color: "#5B9BD5" }}
              >
                Response to Back Office
              </h3>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-700 mb-2 block">
                  Add details and send response to action required
                </Label>
                <textarea
                  value={responseToAdmin}
                  onChange={(e) => setResponseToAdmin(e.target.value)}
                  placeholder="Type your response here... Explain what actions you've taken or provide additional information requested by the admin."
                  className="w-full min-h-[120px] p-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B9BD5] focus:border-transparent resize-none bg-white"
                />
                <p className="text-xs text-gray-500 mt-2">
                  This response will be sent to the back office along with
                  re-uploaded documents.
                </p>
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  className="h-9 px-4 text-sm text-white"
                  style={{ backgroundColor: "#5B9BD5" }}
                  onClick={handleSendResponse}
                  disabled={!responseToAdmin.trim()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Send Response to Back Office
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personal & Basic Details */}
        <Card className="bg-white shadow-sm border border-gray-200 rounded-xl" style={{ gap: "0px" }}>
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
            <User className="w-4 h-4" style={{ color: "#5B9BD5" }} />
            <h3 className="text-sm font-semibold" style={{ color: "#5B9BD5" }}>
              Personal & Basic Information
            </h3>
          </div>
          <CardContent style={{ padding: "16px 24px 10px 24px" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  label="ITS Number"
                  required
                  value={applicantData.itsNo}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Title"
                  value={applicantData.title}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="First Name"
                  required
                  value={applicantData.firstName}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Middle Name"
                  value={applicantData.middleName}
                  disabled
                  placeholder="Middle name"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Last Name"
                  required
                  value={applicantData.lastName}
                  disabled
                  placeholder="Last name"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  type="date"
                  label="Date of Birth"
                  required
                  value={applicantData.dateOfBirth?.split("T")[0]}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Age"
                  value={applicantData.age}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Gender"
                  required
                  value={applicantData.gender}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Birth Place"
                  value={applicantData.birthPlace}
                  disabled
                  placeholder="Birth place"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Nationality"
                  required
                  value={applicantData.nationality}
                  disabled
                  placeholder="Nationality"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Applicant Type"
                  required
                  value={
                    applicantData.applicantType
                      ? applicantData.applicantType.charAt(0).toUpperCase() +
                        applicantData.applicantType.slice(1)
                      : ""
                  }
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Category"
                  value={applicantData.category}
                  disabled
                  placeholder="Category"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Guardian ITS"
                  value={applicantData.guardianITS}
                  disabled
                  placeholder="Enter Guardian ITS"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="HOF ITS"
                  value={applicantData.hofITS}
                  disabled
                  placeholder="Enter HOF ITS"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2" style={{ display: "flex" }}>
                <div className="flex items-center gap-2 mt-4">
                  <Label className="text-sm text-gray-600">Visa Required</Label>
                  <Switch
                    checked={applicantData.visaRequired}
                    disabled
                    className="scale-75"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
              
              {/* <div className="lg:col-span-1">
                <div className="flex items-center gap-2 mt-1">
                  <Label className="text-sm text-gray-600">
                    Miqaat Applicant
                  </Label>
                  <Switch
                    checked={applicantData.isMiqaatApplicant}
                    disabled
                    className="scale-75"
                  />
                </div>
              </div> */}
            </div>
          </CardContent>
        </Card>

        {/* Passport Details */}
        <Card className="bg-white shadow-sm border border-gray-200 rounded-xl" style={{ gap: "0px" }}>
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
            <Globe className="w-4 h-4" style={{ color: "#5B9BD5" }} />
            <h3 className="text-sm font-semibold" style={{ color: "#5B9BD5" }}>
              Passport Information
            </h3>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Number"
                  required
                  value={applicantData.passportNo}
                  disabled
                  placeholder="Passport number"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Type"
                  required
                  value={applicantData.passportType}
                  disabled
                  placeholder="Passport type"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Place of Issue"
                  required
                  value={applicantData.passportIssuePlace}
                  disabled
                  placeholder="Issue place"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Profession"
                  value={applicantData.profession}
                  disabled
                  placeholder="Profession"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Passport First Name"
                  required
                  value={applicantData.passportFirstName}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Middle Name"
                  value={applicantData.passportMiddleName}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Last Name"
                  required
                  value={applicantData.passportLastName}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Arabic Name"
                  value={applicantData.passportArabicName}
                  disabled
                  placeholder="Arabic name"
                  className="border-gray-500 rounded-lg text-sm"
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  type="date"
                  label="Issue Date"
                  required
                  value={applicantData.passportIssueDate}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  type="date"
                  label="Expiry Date"
                  required
                  value={applicantData.passportExpiryDate}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="space-y-2">
                <InputFieldset
                  type="date"
                  label="Passport Date of Birth"
                  required
                  value={applicantData.passportDOB?.split("T")[0]}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Gender"
                  value={applicantData.passportGender}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Nationality"
                  required
                  value={applicantData.passportNationality}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Family Details */}
        <Card className="bg-white shadow-sm border border-gray-200 rounded-xl" style={{ gap: "0px" }}>
          <h2>
            <button
              type="button"
              onClick={toggleFamilySection}
              className={`flex items-center justify-between w-full px-4 py-3 font-medium text-body hover:bg-gray-50 ${
                isFamilyOpen ? "border-b border-gray-200" : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" style={{ color: "#5B9BD5" }} />
                <span
                  className="text-sm font-semibold"
                  style={{ color: "#5B9BD5" }}
                >
                  Family Information
                </span>
              </div>

              {isFamilyOpen ? (
                <ChevronUp className="w-5 h-5 text-blue-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-blue-700" />
              )}
            </button>
          </h2>
          {isFamilyOpen && (
            <CardContent className="p-4">
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Father's Name"
                    value={applicantData.fatherName}
                    disabled
                    placeholder="Father's name"
                    className="border-gray-500 rounded-lg text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Father's Name (Arabic)"
                    value={applicantData.fatherNameArabic}
                    disabled
                    placeholder="Father's name in Arabic"
                    className="border-gray-500 rounded-lg text-sm"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Grandfather's Name"
                    value={applicantData.grandfatherName}
                    disabled
                    placeholder="Grandfather's name"
                    className="border-gray-500 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Grandfather's Name (Arabic)"
                    value={applicantData.grandfatherNameArabic}
                    disabled
                    placeholder="Grandfather's name in Arabic"
                    className="border-gray-500 rounded-lg text-sm"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Mother's Name"
                    value={applicantData.motherName}
                    disabled
                    placeholder="Mother's name"
                    className="border-gray-500 rounded-lg text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Mother's Name (Arabic)"
                    value={applicantData.motherNameArabic}
                    disabled
                    placeholder="Mother's name in Arabic"
                    className="border-gray-500 rounded-lg text-sm"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Mother's Father Name"
                    value={applicantData.mothersFatherName}
                    disabled
                    placeholder="Mother's father name"
                    className="border-gray-500 rounded-lg text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Mother's Father Name (Arabic)"
                    value={applicantData.mothersFatherNameArabic}
                    disabled
                    placeholder="Mother's father name in Arabic"
                    className="border-gray-500 rounded-lg text-sm"
                    dir="rtl"
                  />
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Documents with Re-upload Option */}
        {/* <Card className="bg-white shadow-sm border-2 border-red-300 rounded-xl">
          <div className="bg-red-50 border-b border-red-200 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 md:w-5 md:h-5 text-red-700" />
              <h3 className="text-xs md:text-sm font-semibold text-red-900">
                Documents - Re-upload Required
              </h3>
            </div>
            <Badge className="bg-red-200 text-red-800 text-xs px-2 py-0.5 w-fit">
              {applicantData.documents?.filter(
                (d) =>
                  d.status === "request-more-info" || d.status === "rejected",
              ).length ?? 0}{" "}
              Need Attention
            </Badge>
          </div>
          <CardContent className="p-4">
            <div className="space-y-3">
              {applicantData.documents?.map((doc, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-3 ${
                    doc.status === "request-more-info" ||
                    doc.status === "rejected"
                      ? "border-red-300 bg-red-50/50"
                      : "border-gray-200 bg-gray-50/50"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                
                    <div
                      className={`flex-shrink-0 w-7 h-7 rounded-full border flex items-center justify-center ${
                        doc.status === "request-more-info" ||
                        doc.status === "rejected"
                          ? "bg-red-100 border-red-300"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <span className="text-xs font-semibold text-gray-700">
                        {index + 1}
                      </span>
                    </div>


                    <div className="flex-1 min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-900">
                        {doc.documentType}
                      </p>
                      {doc.fileName && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {documentUploads[doc.documentType]
                            ? `New: ${documentUploads[doc.documentType].name}`
                            : `Current: ${doc.fileName}`}{" "}
                          •{" "}
                          {doc.uploadedDate && (
                            <>
                              {" "}
                              •{" "}
                              {new Date(doc.uploadedDate).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </>
                          )}
                        </p>
                      )}
                    </div>

            
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                  
                      <div className="flex-shrink-0">
                        {doc.status === "approved" && (
                          <Badge className="bg-green-100 text-green-700 border-green-200 text-xs px-2 py-0.5">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approved
                          </Badge>
                        )}
                        {doc.status === "rejected" && (
                          <Badge className="bg-red-100 text-red-700 border-red-200 text-xs px-2 py-0.5">
                            <XCircle className="w-3 h-3 mr-1" />
                            Rejected
                          </Badge>
                        )}
                        {doc.status === "request-more-info" && (
                          <Badge className="bg-red-100 text-red-700 border-red-200 text-xs px-2 py-0.5">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            More Info
                          </Badge>
                        )}
                      </div>

                 
                      <div className="flex items-center gap-2">
                        {doc.fileName && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 md:h-8 px-2 md:px-3 text-xs rounded-md border-gray-300"
                            onClick={() => handleViewDocument(doc.fileName)}
                          >
                            <Eye className="w-3 h-3 md:mr-1" />
                            <span className="hidden md:inline">View</span>
                          </Button>
                        )}

                        {(doc.status === "rejected" ||
                          doc.status === "request-more-info") && (
                          <>
                            <label htmlFor={`doc-upload-${index}`}>
                              <Button
                                type="button"
                                size="sm"
                                className="h-7 md:h-8 px-2 md:px-3 text-xs rounded-md text-white"
                                style={{ backgroundColor: "#5B9BD5" }}
                                onClick={() =>
                                  document
                                    .getElementById(`doc-upload-${index}`)
                                    ?.click()
                                }
                              >
                                <Upload className="w-3 h-3 md:mr-1" />
                                <span className="hidden md:inline">
                                  {documentUploads[doc.documentType]
                                    ? "Change"
                                    : "Re-upload"}
                                </span>
                                <span className="md:hidden">
                                  {documentUploads[doc.documentType]
                                    ? "Chg"
                                    : "Upload"}
                                </span>
                              </Button>
                            </label>
                            <input
                              type="file"
                              accept="image/jpeg, image/jpg"
                              onChange={(e) =>
                                handleDocumentUpload(doc.documentType, e)
                              }
                              className="hidden"
                              id={`doc-upload-${index}`}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                
                  {doc.rejectionRemark && (
                    <div
                      className={`mt-3 sm:ml-10 p-3 rounded-md text-xs border ${
                        doc.status === "rejected"
                          ? "bg-red-100 border-red-300 text-red-800"
                          : "bg-red-100 border-red-300 text-red-800"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-semibold">
                            {doc.status === "rejected"
                              ? "Rejection Reason: "
                              : "Admin Remarks: "}
                          </span>
                          {doc.rejectionRemark}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs text-blue-700">
                📎 <strong>Upload Guidelines:</strong> JPEG format only •
                Maximum 5MB per file • Ensure all text is clearly readable
              </p>
            </div>
          </CardContent>
        </Card> */}

        {/* Action Section */}
        {/* <Card
          className="bg-white shadow-md border-2 rounded-xl"
          style={{ borderColor: "#5B9BD5" }}
        >
          <CardContent className="p-4 md:p-5">
            <div className="flex flex-col sm:flex-row items-start gap-3 mb-4">
              <div
                className="p-2.5 rounded-lg"
                style={{ backgroundColor: "#5B9BD5" }}
              >
                <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
              <div className="flex-1">
                <h4
                  className="text-sm md:text-base font-semibold mb-1.5"
                  style={{ color: "#5B9BD5" }}
                >
                  Action Required - Resubmit Applicant
                </h4>
                <p className="text-xs text-gray-700 leading-relaxed">
                  Please review the admin remarks and re-upload the required
                  documents. Once all documents are updated, click the button
                  below to resubmit this applicant for processing.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2 border-t border-gray-200">
              <Button
                variant="outline"
                className="rounded-lg h-9 px-5 text-sm border-gray-300 hover:bg-gray-50 w-full sm:w-auto"
                onClick={() => onNavigate?.("visa-applicant-management")}
              >
                Cancel
              </Button>
              <Button
                className="rounded-lg h-9 px-5 text-sm text-white font-medium hover:opacity-90 w-full sm:w-auto"
                style={{ backgroundColor: "#5B9BD5" }}
                onClick={handleResubmit}
              >
                <Upload className="w-4 h-4 mr-2" />
                Resubmit Applicant for Processing
              </Button>
            </div>
          </CardContent>
        </Card> */}
      </div>
    </div>
  );
}
